import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import {
  LayoutDashboard,
  LogOut,
  ChevronDown,
  ChevronUp,
  Pin,
  UserPlus,
  Database,
} from "lucide-react";
import { LogoutEmp, GetEmployeeName } from "../../../Auth/Services/authService";
import NormalAlert from "../../../Alerts/NormalAlert";
import ConfirmationAlert from "../../../Alerts/ConfirmationAlert";
import { useNavigate } from "react-router-dom";

const Sidebar = ({ isPinned, setIsPinned, onHoverChange }) => {
  const navigate = useNavigate();
  const hoverTimer = useRef(null);
  const location = useLocation();

  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = isPinned || isHovered;

  const [userName, setUserName] = useState(Cookies.get("username") || "User");
  const [employeeRole, setEmployeeRole] = useState(null);

  const [confirmAlert, setConfirmAlert] = useState(null);
  const [alert, setAlert] = useState(null);

  /*  USER INFO  */
  useEffect(() => {
    const fetchUserName = async () => {
      const token = Cookies.get("employeeToken");
      if (!token) {
        setUserName("User");
        Cookies.remove("username");
        return;
      }

      try {
        const response = await GetEmployeeName(token);
        if (response?.data?.status === "Success") {
          const name = response.data.name;

          Cookies.set("username", name, { secure: true, sameSite: "Strict" });
          setUserName(name);
        } else {
          setUserName("User");
          Cookies.remove("username");
        }
      } catch (error) {
        setAlert({
          type: "fail",
          message:
            error?.response?.data?.message ||
            "Something went wrong. Please try again.",
        });
        setUserName("User");
        Cookies.remove("username");
      }
    };

    fetchUserName();
  }, []);

  /*  LOGOUT  */
  const handleLogout = () => {
    setConfirmAlert({
      type: "warning",
      message: "Are you sure you want to logout?",
    });
  };

  const confirmLogout = async () => {
    try {
      await LogoutEmp(new Date());
    } catch (error) {
      const safeMessage =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      setAlert({
        type: "fail",
        message: safeMessage,
      });
    } finally {
      Cookies.remove("ConsultantToken");
      Cookies.remove("username");
      navigate("/");
    }
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <>
      <aside
        className="
          fixed top-0 left-0 h-screen z-50
          bg-gradient-to-b from-[#0d47a1] to-[#42a5f5]
          text-white flex flex-col
          transition-[width] duration-300 ease-in-out
        "
        style={{ width: isExpanded ? "250px" : "70px" }}
        onMouseEnter={() => {
          if (isPinned) return;

          hoverTimer.current = setTimeout(() => {
            setIsHovered(true);
            onHoverChange?.(true);
          }, 120);
        }}
        onMouseLeave={() => {
          if (isPinned) return;

          clearTimeout(hoverTimer.current);
          setIsHovered(false);
          onHoverChange?.(false);
        }}
      >
        {/*  PROFILE  */}
        <div className="h-[72px] flex items-center border-b border-white/20 px-4">
          {/* ICON COLUMN */}
          <div className="w-10 h-10 flex items-center justify-center">
            <div
              className="
        w-10 h-10 rounded-full
        flex items-center justify-center
        border border-white/70
        font-semibold tracking-wide
      "
            >
              {getInitials(userName)}
            </div>
          </div>
          {/* TEXT */}
          <div
            className={`
      ml-3 transition-all duration-300
      ${
        isExpanded
          ? "opacity-100 translate-x-0"
          : "opacity-0 -translate-x-2 pointer-events-none"
      }
    `}
          >
            <div className="text-sm font-semibold">Welcome</div>
            <div className="text-xs text-white/70">{userName}</div>
          </div>

          {/* PIN */}
          <button
            className={`
      ml-auto transition-all duration-300
      ${isExpanded ? "opacity-100" : "opacity-0 pointer-events-none"}
    `}
            onClick={() => {
              const next = !isPinned;
              setIsPinned(next);
              Cookies.set("sidebarPinned", next ? "true" : "false");
            }}
          >
            <Pin size={18} />
          </button>
        </div>

        {/*  MENU  */}
        <nav
          className={`flex-1 py-2 space-y-2 overflow-y-auto sidebar-scroll ${
            isExpanded ? "px-2" : "px-0"
          }`}
        >

          <MenuItem
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            expanded={isExpanded}
            to="/Admin/Dashboard"
          />

          <MenuItem
            icon={<UserPlus size={20} />}
            label="Add Consultant"
            expanded={isExpanded}
            to="/Admin/Add-Consultant"
          />

          <MenuItem
            icon={<UserPlus size={20} />}
            label="Add Employee"
            expanded={isExpanded}
            to="/Admin/Add-Employee"
          />

          <MenuItem
            icon={<Database size={20} />}
            label="Master Data"
            expanded={isExpanded}
            to="/Admin/Master-Data"
          />

          <MenuItem
            icon={<LayoutDashboard  size={20} />}
            label="Go to Dashboard"
            expanded={isExpanded}
            to="/Dashboard"
          />
        </nav>

        {/*  LOGOUT  */}
        <div className="p-3 border-t border-white/20">
          <button
            onClick={handleLogout}
            className="w-full bg-white text-blue-700 rounded-md flex items-center justify-center gap-2 py-2"
          >
            <LogOut size={18} />
            <span
              className={`transition-all duration-200 ${isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"}`}
            >
              Logout
            </span>
          </button>
        </div>
      </aside>

      {confirmAlert && (
        <ConfirmationAlert
          {...confirmAlert}
          onConfirm={confirmLogout}
          onCancel={() => setConfirmAlert(null)}
        />
      )}

      {alert && <NormalAlert {...alert} onClose={() => setAlert(null)} />}
    </>
  );
};

/*COMPONENTS */

const MenuItem = ({ icon, label, expanded, to }) => (
  <Link
    to={to}
    className={`
      flex items-center rounded-md transition-all duration-200
     ${expanded ? "px-4 gap-3 justify-start" : "px-4 justify-center"}
      hover:bg-white hover:text-blue-800
    `}
    style={{ height: "56px" }}
  >
    <div
      className={`w-10 h-10 flex items-center justify-center shrink-0 ${
        expanded ? "" : "mx-auto"
      }`}
    >
      {icon}
    </div>

    <span
      className={`whitespace-nowrap transition-all duration-200 ${
        expanded
          ? "opacity-100 translate-x-0"
          : "opacity-0 -translate-x-2 w-0 overflow-hidden"
      }`}
    >
      {label}
    </span>
  </Link>
);

const MenuGroup = ({ icon, label, expanded, open, toggle, children }) => (
  <>
    <button
      onClick={toggle}
      className={`w-full flex items-center rounded-md transition-all duration-200 ${expanded ? "px-4 gap-3 justify-start" : "px-4 justify-center"}
 hover:bg-white hover:text-blue-800`}
      style={{ height: "46px" }}
    >
      <div
        className={`w-10 h-10 flex items-center justify-center shrink-0 ${
          expanded ? "" : "mx-auto"
        }`}
      >
        {icon}
      </div>

      <span
        className={`flex-1 text-left whitespace-nowrap transition-all duration-200 ${
          expanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
        }`}
      >
        {label}
      </span>

      {expanded && (open ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
    </button>

    {open && expanded && <div className="mt-1 space-y-1">{children}</div>}
  </>
);

const SubItem = ({ to, label, icon }) => (
  <Link
    to={to}
    className="
      flex items-center gap-3
      h-9
      pl-[48px]
      pr-3
      text-sm text-white/85
      rounded-md
      hover:bg-white/10 hover:text-white
      transition-colors
    "
  >
    <span className="w-5 h-5 flex items-center justify-center shrink-0">
      {icon}
    </span>
    <span className="truncate">{label}</span>
  </Link>
);

export default Sidebar;
